// src/features/feedback/pages/FeedbackDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Block,
  HourglassEmpty,
  Help,
  Person,
  Business,
  CalendarToday,
  Update,
  BarChart,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

// Hooks
import { useFeedback } from '../hooks/useFeedback';
import { useFeedbackManagement, useFeedbackStatus } from '../hooks/useFeedbackManagement';
import { usePermissions } from '../../auth/hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';

// Components
import FeedbackManagementDialog from '../components/FeedbackManagementDialog';
import VoteButton from '../components/VoteButton';
import VoteStatisticsDialog from '../components/VoteStatisticsDialog';
import { dateHintUtils } from '../../../components/common/DateWithHint';

// Hooks for confirmation dialog
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { parseApiTimestamp } from '../../../utils/formatters/dateFormatter';
import PageHeader from '../../../components/common/PageHeader';

const getStatusIcon = (status) => {
  switch (status) {
    case 'FeedbackStatus.IMPLEMENTED':
      return <CheckCircle />;
    case 'FeedbackStatus.WILL_NOT_IMPLEMENT':
      return <Block />;
    case 'FeedbackStatus.PENDING_REVIEW':
      return <HourglassEmpty />;
    case 'FeedbackStatus.NOT_DECIDED':
    default:
      return <Help />;
  }
};

export default function FeedbackDetailsPage() {
  const { feedbackId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { t } = useTranslation(['feedback', 'common']);
  const { user } = useAuth();
  const permissions = usePermissions();

  // State
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);
  const [statisticsDialogOpen, setStatisticsDialogOpen] = useState(false);
  
  // Hooks
  const { feedbackDetails, loading, fetchFeedbackDetails, clearFeedbackDetails } = useFeedback();
  const { deleteFeedback, deleting } = useFeedbackManagement();
  const {
    getImplementationColor,
    getImplementationLabel,
    getVisibilityColor,
    getVisibilityLabel,
  } = useFeedbackStatus();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // Check if user can moderate feedback
  const canModerate = permissions.hasScope('feedback:moderate');
  const isOwner = feedbackDetails?.staff_member_id === user?.staff_member_id;

  // Fetch feedback details on mount
  useEffect(() => {
    if (feedbackId) {
      fetchFeedbackDetails(feedbackId);
    }

    return () => {
      clearFeedbackDetails();
    };
  }, [feedbackId, fetchFeedbackDetails, clearFeedbackDetails]);

  const handleBack = () => {
    navigateBack('/feedback');
  };

  const handleManage = () => {
    setManagementDialogOpen(true);
  };

  const handleViewStatistics = () => {
    setStatisticsDialogOpen(true);
  };

  const handleDeleteClick = () => {
    showConfirmDialog({
      title: t('feedback:details.delete_title', 'Delete Feedback'),
      message: t('feedback:details.delete_message', 'Are you sure you want to delete this feedback? This action cannot be undone.'),
      confirmText: t('common:buttons.delete', 'Delete'),
      cancelText: t('common:buttons.cancel', 'Cancel'),
      severity: 'error',
      onConfirm: handleDeleteConfirm,
    });
  };

  const handleDeleteConfirm = async () => {
    const result = await deleteFeedback(feedbackId);
    if (result.success) {
      navigate('/feedback');
    }
  };

  const handleManagementSuccess = () => {
    // Refresh feedback details after management changes
    fetchFeedbackDetails(feedbackId);
  };

  const handleVoteChange = (data) => {
    // Refresh feedback details to get updated vote count
    fetchFeedbackDetails(feedbackId);
  };

  // Helper function to get relative date text
  const getRelativeDateText = (date) => {
    const { hint } = dateHintUtils.getDateWithHint(
      date,
      '',
      { dateFormat: 'd.M.yyyy' },
      t
    );
    return hint || '';
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (!feedbackDetails) {
    return (
      <Box>
        <Alert severity="error">
          {t('feedback:messages.fetch_error', 'Failed to load feedback details')}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          {t('common:buttons.back', 'Back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={handleBack}
        title={t('feedback:details.title', 'Feedback Details')}
        crumb={feedbackDetails?.title}
        subtitle={t('feedback:details.subtitle', 'View feedback information and status')}
        actions={<>
          {/* Admin Actions */}
          {canModerate && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<BarChart />}
                onClick={handleViewStatistics}
              >
                {t('feedback:details.view_stats', 'Vote Stats')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleManage}
              >
                {t('feedback:details.manage', 'Manage')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteClick}
              >
                {t('common:buttons.delete', 'Delete')}
              </Button>
            </Box>
          )}
        </>}
      />

      <Stack spacing={3}>
        {/* Main Feedback Card */}
        <Card>
          <CardContent>
            {/* Title, Status, and Vote Button */}
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {feedbackDetails.title}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  <Chip
                    icon={getStatusIcon(feedbackDetails.status)}
                    label={getImplementationLabel(feedbackDetails.status)}
                    color={getImplementationColor(feedbackDetails.status)}
                    variant="filled"
                  />
                  {canModerate && (
                    <Chip
                      icon={feedbackDetails.visibility === 'FeedbackVisibility.PUBLISHED' ? <Visibility /> : <VisibilityOff />}
                      label={getVisibilityLabel(feedbackDetails.visibility)}
                      color={getVisibilityColor(feedbackDetails.visibility)}
                      variant="filled"
                    />
                  )}
                  {isOwner && (
                    <Chip
                      label={t('feedback:details.your_feedback', 'Your Feedback')}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
              
              {/* Vote Button */}
              <Box ml={2}>
                <VoteButton
                  feedbackId={feedbackDetails.feedback_id}
                  userHasVoted={feedbackDetails.user_has_voted}
                  totalVotes={feedbackDetails.total_votes || 0}
                  onVoteChange={handleVoteChange}
                  variant="button"
                  size="large"
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Description */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('feedback:details.description', 'Description')}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {feedbackDetails.description}
              </Typography>
            </Box>

            {/* Admin Response */}
            {feedbackDetails.admin_response && (
              <>
                <Divider sx={{ my: 2 }} />
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: 'action.hover',
                    borderLeft: 4,
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {t('feedback:details.admin_response', 'Administrator Response')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {feedbackDetails.admin_response}
                  </Typography>
                  {feedbackDetails.reviewed_by_name && (
                    <Box display="flex" alignItems="center" gap={1} mt={2}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                        <Person sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        {t('feedback:details.reviewed_by', 'Reviewed by {{name}}', {
                          name: feedbackDetails.reviewed_by_name
                        })}
                        {feedbackDetails.reviewed_at && (
                          <> • {getRelativeDateText(feedbackDetails.reviewed_at)}</>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </CardContent>
        </Card>

        {/* Metadata Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t('feedback:details.information', 'Information')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" gap={4} flexWrap="wrap">
              {/* Left Column - User & Department Info */}
              <Box flex={1} minWidth={250}>
                <Stack spacing={2}>
                  {/* Submitted By */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Person color="action" />
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        {t('feedback:details.submitted_by', 'Submitted By')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {feedbackDetails.staff_member_name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Department */}
                  {canModerate && (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Business color="action" />
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          {t('feedback:details.department', 'Department')}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {feedbackDetails.department_name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Right Column - Date Info */}
              <Box flex={1} minWidth={250}>
                <Stack spacing={2}>
                  {/* Created Date */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <CalendarToday color="action" />
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        {t('feedback:details.created_date', 'Created Date')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {format(parseApiTimestamp(feedbackDetails.created_at), 'PPpp')}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        ({getRelativeDateText(feedbackDetails.created_at)})
                      </Typography>
                    </Box>
                  </Box>

                  {/* Last Updated */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Update color="action" />
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        {t('feedback:details.last_updated', 'Last Updated')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {format(parseApiTimestamp(feedbackDetails.updated_at), 'PPpp')}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        ({getRelativeDateText(feedbackDetails.updated_at)})
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* Management Dialog (Admin Only) */}
      {canModerate && (
        <>
          <FeedbackManagementDialog
            open={managementDialogOpen}
            onClose={() => setManagementDialogOpen(false)}
            feedback={feedbackDetails}
            onSuccess={handleManagementSuccess}
          />
          
          <VoteStatisticsDialog
            open={statisticsDialogOpen}
            onClose={() => setStatisticsDialogOpen(false)}
            feedbackId={feedbackDetails.feedback_id}
            feedbackTitle={feedbackDetails.title}
          />
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </Box>
  );
}